import { SingleValueCookie } from 'saddlebag-cookie';

import type { KetchConsentCookie } from './ketch-types';

/**
 * Gets the Ketch consent cookie value from either a cookie string or document.cookie.
 *
 * @param cookieName - The cookie name to look up
 * @param cookieString - Optional full cookie header string. If provided, extracts
 *                       the _swb_consent_ value from it. If omitted, reads from
 *                       document.cookie (browser environment).
 * @returns The raw cookie value, or null if not found
 */
export function getKetchCookieValue(
  cookieName: string,
  cookieString?: string | null,
): string | null {
  if (cookieString !== undefined) {
    // Server-side: extract from provided cookie string
    return extractCookieValue(cookieString, cookieName);
  }
  // Browser: read from document.cookie
  return getCookieValue(cookieName);
}

/**
 * Parses the _swb_consent_ cookie value.
 * Format: URL-encoded Base64 JSON
 *
 * @param cookieValue - Raw cookie value
 * @returns Parsed KetchConsentCookie object
 * @throws Error if cookie is malformed (invalid base64 or JSON)
 */
export function parseKetchConsentCookie(
  cookieValue: string,
): KetchConsentCookie {
  const urlDecoded = decodeURIComponent(cookieValue);
  const base64Decoded = atob(urlDecoded);
  return JSON.parse(base64Decoded);
}

/**
 * Checks whether a consent cookie value represents active consent.
 * Returns true when consent is effectively in place (banner should not be shown).
 * Returns false when consent is missing or pending (banner should be shown).
 *
 * Decision logic for interactive === false:
 * - Has non-essential purposes → non-banner market with auto-applied consent → true
 * - Only essential_services → banner market pre-consent → false
 *
 * @param cookieValue - Raw cookie value (URL-encoded base64 JSON)
 * @returns true if consent is active, false otherwise
 */
export function hasActiveConsent(cookieValue: string): boolean {
  try {
    const decoded = parseKetchConsentCookie(cookieValue);
    const purposes = decoded.purposes || {};
    const purposeKeys = Object.keys(purposes);
    if (purposeKeys.length === 0) {
      return false;
    }
    if (decoded.interactive === false) {
      const hasNonEssentialPurposes = purposeKeys.some(
        (key) => key !== 'essential_services',
      );
      if (!hasNonEssentialPurposes) {
        return false;
      }
      return true;
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * Extracts a single cookie value from a cookie header string.
 * Used for server-side cookie parsing.
 *
 * @param cookieString - Full cookie header string (e.g., "cookie1=value1; cookie2=value2")
 * @param name - Cookie name to extract
 * @returns The cookie value, or null if not found
 */
function extractCookieValue(
  cookieString: string | null,
  name: string,
): string | null {
  if (!cookieString) {
    return null;
  }
  const cookies = cookieString.split(';');
  for (const cookie of cookies) {
    const [cookieName, ...valueParts] = cookie.trim().split('=');
    if (cookieName === name) {
      return valueParts.join('='); // Handle '=' in value
    }
  }
  return null;
}

/**
 * Gets a cookie value by name from document.cookie using saddlebag-cookie.
 *
 * @param name - Cookie name
 * @returns Cookie value or null if not found
 */
function getCookieValue(name: string): string | null {
  if (typeof document === 'undefined') {
    return null;
  }
  return new SingleValueCookie(name).getValue() ?? null;
}
